import React from "react";
import {
  SiGooglecloud,
  SiAlibabacloud,
  SiDigitalocean,
  SiCloudflare,
  SiKubernetes,
  SiDocker,
  SiRedhat,
  SiVmware,
  SiCisco,
  SiUbuntu,
  SiDebian,
  SiLinux,
  SiPostgresql,
  SiMysql,
  SiMongodb,
  SiRedis,
  SiElastic,
  SiTerraform,
  SiGitlab,
  SiOkta,
  SiSap,
  SiNginx,
  SiOpenstack,
  SiSnowflake,
  SiFortinet,
} from "react-icons/si";
import { FaAws, FaWindows } from "react-icons/fa";
import { DiMsqlServer } from "react-icons/di";
import { VscAzure } from "react-icons/vsc";
import { GrOracle } from "react-icons/gr";
import { Cloud } from "lucide-react";

type BrandIcon = React.ComponentType<{ className?: string }>;

export interface CloudBrand {
  id: string;
  label: string;
  Icon: BrandIcon;
  hex: string;
  rgb: string;
  onLight: string;
  onDark: string;
  match: RegExp;
}

interface CloudBrandSeed {
  id: string;
  label: string;
  Icon: BrandIcon;
  hex: string;
  match: RegExp;
}

function hexToChannels(hex: string): [number, number, number] {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function hexToRgbTriplet(hex: string): string {
  return hexToChannels(hex).join(",");
}

function rgbTripletToHex(rgb: string): string {
  const channels = rgb.split(",").map((c) => Number(c.trim()) || 0);
  return `#${channels
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0"))
    .join("")}`;
}

export function accentTextVariants(rgb: string): { onLight: string; onDark: string } {
  const hex = rgbTripletToHex(rgb);
  return { onLight: withLightness(hex, 0, 0.3), onDark: withLightness(hex, 0.62, 1) };
}

function withLightness(hex: string, min: number, max: number): string {
  const [r, g, b] = hexToChannels(hex).map((c) => c / 255);
  const cMax = Math.max(r, g, b);
  const cMin = Math.min(r, g, b);
  const delta = cMax - cMin;
  const l = (cMax + cMin) / 2;
  const target = Math.min(max, Math.max(min, l));
  if (target === l) return hex;

  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  let hue = 0;
  if (delta !== 0) {
    if (cMax === r) hue = ((g - b) / delta) % 6;
    else if (cMax === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
  }
  hue *= 60;
  if (hue < 0) hue += 360;

  const c = (1 - Math.abs(2 * target - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = target - c / 2;
  const [r1, g1, b1] =
    hue < 60 ? [c, x, 0]
      : hue < 120 ? [x, c, 0]
        : hue < 180 ? [0, c, x]
          : hue < 240 ? [0, x, c]
            : hue < 300 ? [x, 0, c]
              : [c, 0, x];
  const toHex = (v: number) =>
    Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
}

const CLOUD_PLATFORM_TOKENS =
  /\b(aws|amazon|eks|azure|entra|aks|gcp|google|gke|oracle|oci|ibm|alibaba|aliyun|digital\s*ocean|cloudflare)\b/;

function unlessCloudPlatform(pattern: RegExp): RegExp {
  return new RegExp(
    `^(?!.*${CLOUD_PLATFORM_TOKENS.source}).*(?:${pattern.source})`,
  );
}

const CLOUD_BRAND_SEEDS: CloudBrandSeed[] = [
  { id: "aws", label: "Amazon Web Services", Icon: FaAws, hex: "#FF9900", match: /\b(aws|amazon|eks)\b/ },
  {
    id: "mssql",
    label: "Microsoft SQL Server",
    Icon: DiMsqlServer,
    hex: "#CC2927",
    match: unlessCloudPlatform(/\b(microsoft\s*sql|ms\s*sql|mssql|sql\s*server)\b/),
  },
  {
    id: "windows",
    label: "Microsoft Windows",
    Icon: FaWindows,
    hex: "#00A4EF",
    match: unlessCloudPlatform(/\bwindows\b/),
  },
  { id: "azure", label: "Microsoft Azure", Icon: VscAzure, hex: "#0078D4", match: /\b(azure|microsoft|entra|aks)\b/ },
  { id: "gcp", label: "Google Cloud", Icon: SiGooglecloud, hex: "#34A853", match: /\b(gcp|gco|google|gke)\b/ },
  { id: "oracle", label: "Oracle Cloud", Icon: GrOracle, hex: "#C74634", match: /\b(oracle|oci)\b/ },
  { id: "ibm", label: "IBM Cloud", Icon: Cloud, hex: "#0F62FE", match: /\bibm\b/ },
  { id: "alibaba", label: "Alibaba Cloud", Icon: SiAlibabacloud, hex: "#FF6A00", match: /\b(alibaba|aliyun)\b/ },
  { id: "digitalocean", label: "DigitalOcean", Icon: SiDigitalocean, hex: "#0080FF", match: /\bdigital\s*ocean\b/ },
  { id: "cloudflare", label: "Cloudflare", Icon: SiCloudflare, hex: "#F38020", match: /\bcloudflare\b/ },
  { id: "redhat", label: "Red Hat", Icon: SiRedhat, hex: "#EE0000", match: /\b(red\s*hat|rhel|openshift)\b/ },
  { id: "kubernetes", label: "Kubernetes", Icon: SiKubernetes, hex: "#326CE5", match: /\b(kubernetes|k8s)\b/ },
  { id: "docker", label: "Docker", Icon: SiDocker, hex: "#2496ED", match: /\bdocker\b/ },
  { id: "vmware", label: "VMware", Icon: SiVmware, hex: "#607078", match: /\b(vmware|vsphere|esxi)\b/ },
  { id: "cisco", label: "Cisco", Icon: SiCisco, hex: "#1BA0D7", match: /\bcisco\b/ },
  { id: "openstack", label: "OpenStack", Icon: SiOpenstack, hex: "#ED1944", match: /\bopenstack\b/ },
  { id: "terraform", label: "Terraform", Icon: SiTerraform, hex: "#7B42BC", match: /\bterraform\b/ },
  { id: "ubuntu", label: "Ubuntu", Icon: SiUbuntu, hex: "#E95420", match: /\bubuntu\b/ },
  { id: "debian", label: "Debian", Icon: SiDebian, hex: "#A81D33", match: /\bdebian\b/ },
  { id: "linux", label: "Linux", Icon: SiLinux, hex: "#D8A400", match: /\blinux\b/ },
  { id: "postgresql", label: "PostgreSQL", Icon: SiPostgresql, hex: "#4169E1", match: /\bpostgres(ql)?\b/ },
  { id: "mysql", label: "MySQL", Icon: SiMysql, hex: "#4479A1", match: /\b(mysql)\b/ },
  { id: "mongodb", label: "MongoDB", Icon: SiMongodb, hex: "#47A248", match: /\bmongo(db)?\b/ },
  { id: "redis", label: "Redis", Icon: SiRedis, hex: "#FF4438", match: /\bredis\b/ },
  { id: "elastic", label: "Elastic", Icon: SiElastic, hex: "#00A3E0", match: /\belastic(search|stack)?\b/ },
  { id: "nginx", label: "NGINX", Icon: SiNginx, hex: "#009639", match: /\bnginx\b/ },
  { id: "gitlab", label: "GitLab", Icon: SiGitlab, hex: "#FC6D26", match: /\bgitlab\b/ },
  { id: "okta", label: "Okta", Icon: SiOkta, hex: "#007DC1", match: /\bokta\b/ },
  { id: "sap", label: "SAP", Icon: SiSap, hex: "#0FAAFF", match: /\bsap\b/ },
  { id: "snowflake", label: "Snowflake", Icon: SiSnowflake, hex: "#29B5E8", match: /\bsnowflake\b/ },
  { id: "fortinet", label: "Fortinet", Icon: SiFortinet, hex: "#EE3124", match: /\b(fortinet|fortigate)\b/ },
];

export const CLOUD_BRANDS: CloudBrand[] = CLOUD_BRAND_SEEDS.map((b) => ({
  ...b,
  rgb: hexToRgbTriplet(b.hex),
  onLight: withLightness(b.hex, 0, 0.3),
  onDark: withLightness(b.hex, 0.62, 1),
}));

export const MAJOR_CSP_IDS = [
  "aws",
  "azure",
  "gcp",
  "oracle",
  "ibm",
  "alibaba",
  "digitalocean",
  "cloudflare",
] as const;

const brandById = new Map(CLOUD_BRANDS.map((b) => [b.id, b]));

export function getCloudBrand(id: string): CloudBrand | null {
  return brandById.get(id) ?? null;
}

const resolvedByName = new Map<string, CloudBrand | null>();

export function resolveCloudBrand(name?: string | null): CloudBrand | null {
  const key = (name || "").trim().toLowerCase();
  if (!key) return null;
  const cached = resolvedByName.get(key);
  if (cached !== undefined) return cached;
  const found = CLOUD_BRANDS.find((b) => b.match.test(key)) ?? null;
  resolvedByName.set(key, found);
  return found;
}

export function CloudBrandLogo({
  brand,
  className = "size-4",
}: {
  brand: CloudBrand;
  className?: string;
}) {
  const { Icon } = brand;
  return (
    <span aria-label={brand.label} role="img" style={{ color: brand.hex }} className="inline-flex">
      <Icon className={className} />
    </span>
  );
}

export function CloudBrandBadge({
  brand,
  className = "size-9",
  iconClassName = "size-[18px]",
}: {
  brand: CloudBrand;
  className?: string;
  iconClassName?: string;
}) {
  const { Icon } = brand;
  return (
    <span
      role="img"
      aria-label={brand.label}
      className={`flex flex-shrink-0 items-center justify-center rounded-xl ${className}`}
      style={{
        color: brand.hex,
        backgroundColor: `rgba(${brand.rgb},0.14)`,
        boxShadow: `inset 0 0 0 1px rgba(${brand.rgb},0.28)`,
      }}
    >
      <Icon className={iconClassName} />
    </span>
  );
}

export function CloudBrandArt({ brand }: { brand: CloudBrand }) {
  const { Icon } = brand;
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.11] dark:opacity-[0.22] z-0"
        style={{
          background: `linear-gradient(135deg, rgba(${brand.rgb},1) 0%, rgba(${brand.rgb},0.45) 42%, transparent 78%)`,
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 size-52 rounded-full blur-3xl opacity-40 dark:opacity-50 z-0"
        style={{ background: `radial-gradient(circle, rgba(${brand.rgb},0.55), transparent 70%)` }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute right-3 top-[54%] -translate-y-1/2 flex items-center justify-center opacity-[0.14] dark:opacity-[0.22] z-0"
        style={{ color: brand.hex }}
      >
        <Icon className="size-20" />
      </span>
    </>
  );
}
